import { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Clock3,
  MapPin,
  PenSquare,
  Phone,
  RefreshCw,
  Trash2,
  Zap,
} from "lucide-react";

import { STATUS } from "../config/taskData";
import { formatDate, getAvatarColor, getDeadlineCountdown } from "../utils/tasks";
import { InlineIcon, UiIcon } from "./Icon";
import UserProfileButton from "./UserProfileButton";

import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import phoneInputLabels from 'react-phone-number-input/locale/pl'

export default function ExecutorScreen({
  auth,
  userTier,
  task,
  contractors,
  onStatus,
  onBack,
  onUpdateTask,
  onUpdateContractor,
  onDeleteTask,
  onProfileClick,
  openContractorEditorOnLoad = false,
}) {
  const st = STATUS[task.status];
  const isDone = task.status === "done";
  const isUnassigned = !task.contact || task.contact.id === -1;
  const isSelf = task.contact && task.contact.id === 0;
  const isOwner = task.is_owner ?? true; // default to true for backward compatibility
  const deadlineCountdown = getDeadlineCountdown(task.deadlineAt || task.date, task.time);
  const displayDeadlineCountdown = deadlineCountdown?.replace(/^Za\s+/, "");
  const isDeadlineOverdue = deadlineCountdown.startsWith("Po terminie");
  const avatarColor = task.contact
    ? getAvatarColor(task.contact.id)
    : "var(--color-text-disabled)";
  const [showContractorEditor, setShowContractorEditor] = useState(false);
  const [autoOpenConsumed, setAutoOpenConsumed] = useState(false);
  const [contractorDraft, setContractorDraft] = useState({
    name: task.contact?.name || "",
    phone: task.contact?.phone || "",
    position: task.contact?.position || task.contact?.role || "",
    company: task.contact?.company || "",
  });
  const [contractorError, setContractorError] = useState("");
  const [savingContractor, setSavingContractor] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deletingTask, setDeletingTask] = useState(false);
  const [showTaskEditor, setShowTaskEditor] = useState(false);
  const [taskDraft, setTaskDraft] = useState({
    desc: task.desc || "",
    date: task.date || "",
    time: task.time || "",
    assignee: task.contact
      ? task.contact.id === 0
        ? "self"
        : String(task.contact.id)
      : "unassigned",
  });
  const [taskError, setTaskError] = useState("");
  const [savingTask, setSavingTask] = useState(false);

  const hourOptions = Array.from({ length: 24 }, (_, index) =>
    index.toString().padStart(2, "0")
  );
  const minuteOptions = ["00", "15", "30", "45"];

  const splitTime = (timeValue) => {
    if (!timeValue) {
      return { hour: "", minute: "" };
    }

    const [hour, minute] = timeValue.split(":");
    return { hour: hour || "", minute: minute || "" };
  };

  const getTimeValue = (hour, minute) => {
    if (!hour && !minute) {
      return "";
    }
    return `${hour || "00"}:${minute || "00"}`;
  };

  useEffect(() => {
    setTaskDraft({
      desc: task.desc || "",
      date: task.date || "",
      time: task.time || "",
      assignee: task.contact
        ? task.contact.id === 0
          ? "self"
          : String(task.contact.id)
        : "unassigned",
    });
    setTaskError("");
  }, [task.id, task.desc, task.date, task.time, task.contact]);

  useEffect(() => {
    if (
      openContractorEditorOnLoad &&
      !autoOpenConsumed &&
      !isUnassigned &&
      !isSelf &&
      task.contact
    ) {
      setContractorDraft({
        name: task.contact.name || "",
        phone: task.contact.phone || "",
        position: task.contact.position || task.contact.role || "",
        company: task.contact.company || "",
      });
      setContractorError("");
      setShowContractorEditor(true);
      setAutoOpenConsumed(true);
    }
  }, [openContractorEditorOnLoad, autoOpenConsumed, isUnassigned, isSelf, task.contact]);

  const actions = {
    new: [
      {
        label: "Przyjmuję zadanie",
        icon: Check,
        next: "in_progress",
        color: "var(--color-primary)",
        bg: "var(--color-primary-light)",
        border: "var(--color-primary-border)",
      },
    ],
    in_progress: [
      {
        label: "Zadanie wykonane",
        icon: Check,
        next: "done",
        color: "var(--badge-done-text)",
        bg: "var(--badge-done-bg)",
        border: "var(--badge-done-border)",
      },
      {
        label: "Zgłoś problem",
        icon: AlertTriangle,
        next: "problem",
        color: "var(--badge-problem-text)",
        bg: "var(--badge-problem-bg)",
        border: "var(--badge-problem-border)",
      },
    ],
    done: [],
    problem: [
      {
        label: "Wznawiam realizację",
        icon: RefreshCw,
        next: "in_progress",
        color: "var(--badge-progress-text)",
        bg: "var(--badge-progress-bg)",
        border: "var(--badge-progress-border)",
      },
    ],
  };

  const saveContractor = async () => {
    if (!task.contact?.id) return;
    if (!contractorDraft.name.trim()) {
      setContractorError("Nazwa wykonawcy jest wymagana.");
      return;
    }

    setContractorError("");
    setSavingContractor(true);
    let result;
    try {
      result = await onUpdateContractor(task.contact.id, {
        name: contractorDraft.name.trim(),
        phone: contractorDraft.phone || null,
        position: contractorDraft.position.trim() || null,
        company: contractorDraft.company.trim() || null,
      });
    } finally {
      setSavingContractor(false);
    }

    if (!result?.ok) {
      if (result.error && result.error.includes("Incorrect phone number format")) {
        setContractorError("Wprowadź poprawny numer telefonu (np. +1234567890).");
        return;
      }
      setContractorError(result?.error || "Nie udało się zapisać wykonawcy.");
      return;
    }
    setShowContractorEditor(false);
  };

  const handleDeleteTask = async () => {
    if (!onDeleteTask) return;

    const confirmed = window.confirm("Czy na pewno chcesz usunąć to zadanie?");
    if (!confirmed) return;

    setDeleteError("");
    setDeletingTask(true);
    const result = await onDeleteTask(task.id);
    setDeletingTask(false);

    if (!result?.ok) {
      setDeleteError(result?.error || "Nie udało się usunąć zadania.");
    }
  };

  const saveTask = async () => {
    if (!onUpdateTask) return;

    if (!taskDraft.desc.trim()) {
      setTaskError("Opis zadania jest wymagany.");
      return;
    }

    setTaskError("");
    setSavingTask(true);
    let result;
    try {
      result = await onUpdateTask(task.id, {
        desc: taskDraft.desc.trim(),
        date: taskDraft.date,
        time: taskDraft.time,
        assignee: taskDraft.assignee,
      });
    } finally {
      setSavingTask(false);
    }

    if (!result?.ok) {
      setTaskError(result?.error || "Nie udało się zapisać zmian zadania.");
      return;
    }

    setShowTaskEditor(false);
  };

  return (
    <div
      style={{
        fontFamily: "var(--font-body)",
        background: "var(--color-bg-app)",
        minHeight: "100dvh",
        maxWidth: 480,
        width: "calc(100% - 16px)",
        margin: "0 auto",
      }}
    >
      <div
        style={{
          background: "var(--color-bg-card)",
          borderBottom: "1px solid var(--color-border)",
          padding: "16px 20px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              color: "var(--color-text-muted)",
              cursor: "pointer",
              fontSize: "var(--text-sm)",
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: 0,
              fontFamily: "inherit",
            }}
          >
            <UiIcon Icon={ArrowLeft} style={{ marginRight: 2 }} /> Wróć do dashboardu
          </button>
          <UserProfileButton auth={auth} userTier={userTier} onProfileClick={onProfileClick} />
        </div>
        <div
          style={{
            marginTop: 8,
            fontFamily: "var(--font-display)",
            fontSize: "var(--text-h3)",
            fontWeight: "var(--font-semibold)",
            color: "var(--color-text-primary)",
            lineHeight: 1.2,
          }}
        >
          {task.name || "Zadanie"}
        </div>
        {!isSelf && !isUnassigned && (
          <div
            style={{
              fontSize: "var(--text-xs)",
              color: "var(--color-text-muted)",
              marginTop: 4,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
          </div>
        )}
      </div>

      <div style={{ padding: 20 }}>
        <div
          style={{
            background: st.bg,
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            gap: 10,
            border: `1px solid ${st.border}`,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: st.dot,
              display: "inline-block",
              flexShrink: 0,
            }}
          />
          <span style={{ color: st.color, fontWeight: "var(--font-semibold)", fontSize: "var(--text-xs)" }}>
            {st.label}
          </span>
        </div>

        <div
          style={{
            background: "var(--color-bg-card)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-lg)",
            padding: 20,
            marginBottom: 16,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            position: "relative",
          }}
        >
          {isOwner && (
            <button
              type="button"
              onClick={() => {
                setTaskDraft({
                  desc: task.desc || "",
                  date: task.date || "",
                  time: task.time || "",
                  assignee: task.contact
                    ? task.contact.id === 0
                      ? "self"
                      : String(task.contact.id)
                    : "unassigned",
                });
                setTaskError("");
                setShowTaskEditor(true);
              }}
              style={{
                border: "1px solid var(--btn-secondary-border)",
                background: "var(--color-bg-card)",
                color: "var(--btn-secondary-text)",
                borderRadius: "var(--radius-md)",
                padding: "6px 8px",
                cursor: "pointer",
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                top: 12,
                right: 12,
              }}
              title="Edytuj zadanie"
            >
              <UiIcon Icon={PenSquare} />
            </button>
          )}
          <p
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-h3)",
              color: "var(--color-text-primary)",
              fontWeight: "var(--font-semibold)",
              lineHeight: "var(--leading-text)",
              marginTop: 0,
              marginBottom: 12,
              paddingRight: 44,
            }}
          >
            {task.desc || "Brak opisu"}
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {task.priority === "high" && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: "var(--color-orange-bg)",
                  color: "var(--color-orange)",
                  padding: "4px 10px",
                  borderRadius: 8,
                  fontSize: "var(--text-sm)",
                  fontWeight: 700,
                }}
              >
                <InlineIcon Icon={Zap} style={{ marginRight: 4 }} />
                Pilne
              </div>
            )}
            {(task.date || task.time) && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "flex-start",
                    gap: 8,
                    background: "var(--color-bg-subtle)",
                    color: !isDone && isDeadlineOverdue ? "var(--color-warning)" : "var(--color-text-muted)",
                    padding: "8px 10px",
                    borderRadius: "var(--radius-md)",
                    fontSize: "var(--text-xs)",
                    fontWeight: "var(--font-medium)",
                    border: "1px solid var(--color-border)",
                    lineHeight: 1.4,
                  }}
                >
                  <InlineIcon Icon={Clock3} style={{ marginTop: 1 }} />
                  <div>
                    <div>
                      Do: {task.date && formatDate(task.deadlineAt || task.date)}
                      {task.time ? ` ${task.time}` : ""}
                    </div>
                    {!isDone && task.date && (
                      <div>
                        Pozostało: {isDeadlineOverdue ? "Po terminie" : displayDeadlineCountdown}
                      </div>
                    )}
                  </div>
                </div>
                {task.location && (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: "var(--color-bg-subtle)",
                      color: "var(--color-text-muted)",
                      padding: "4px 10px",
                      borderRadius: "var(--radius-md)",
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-medium)",
                      border: "1px solid var(--color-border)",
                    }}
                  >
                    <InlineIcon Icon={MapPin} style={{ marginRight: 2 }} />
                    {task.location}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 28,
            padding: "14px 16px",
            background: isUnassigned ? "var(--color-warning-bg)" : "var(--color-bg-card)",
            border: `1px solid ${isUnassigned ? "color-mix(in srgb, var(--color-warning) 28%, white)" : "var(--color-border)"}`,
            borderRadius: "var(--radius-lg)",
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (isUnassigned || isSelf || !task.contact || !isOwner) return;
              setContractorDraft({
                name: task.contact.name || "",
                phone: task.contact.phone || "",
                position: task.contact.position || task.contact.role || "",
                company: task.contact.company || "",
              });
              setContractorError("");
              setShowContractorEditor(true);
            }}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: 0,
              background: "none",
              border: "none",
              cursor: isUnassigned || isSelf || !task.contact || !isOwner ? "default" : "pointer",
              textAlign: "left",
              fontFamily: "inherit",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: avatarColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: isUnassigned ? 18 : 15,
                fontWeight: 700,
                color: "var(--color-white)",
                flexShrink: 0,
              }}
            >
              {isUnassigned ? "?" : task.contact.name[0]}
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: "var(--text-body)",
                  fontWeight: "var(--font-medium)",
                  color: isUnassigned ? "var(--color-warning)" : "var(--color-text-primary)",
                }}
              >
                {isUnassigned
                  ? "Nieprzypisane"
                  : isSelf
                  ? "Ja (Koordynator)"
                  : task.contact.name}
              </div>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
                {isUnassigned
                  ? "Wymaga przypisania do osoby"
                  : isSelf
                  ? "Moje zadanie"
                  : (
                    <>
                      {task.contact.position ? (
                        <strong>{task.contact.position}</strong>
                      ) : (
                        "Wykonawca"
                      )}
                      {task.contact.company ? ` | ${task.contact.company}` : ""}
                    </>
                  )}
              </div>
              {!isUnassigned && !isSelf && task.contact.phone && (
                <div
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--color-primary)",
                    fontWeight: "var(--font-medium)",
                    marginTop: 2,
                  }}
                >
                  <InlineIcon Icon={Phone} style={{ marginRight: 4 }} />
                  {task.contact.phone}
                </div>
              )}
              {!isUnassigned && !isSelf && isOwner && (
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginTop: 2 }}>
                  Kliknij nazwę, aby edytować dane
                </div>
              )}
            </div>
          </button>

          {!isUnassigned && !isSelf && task.contact.phone && (
            <a
              href={`tel:${task.contact.phone}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                background: "var(--color-success)",
                color: "var(--color-white)",
                border: "none",
                padding: "10px 14px",
                borderRadius: "var(--radius-md)",
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-medium)",
                textDecoration: "none",
                flexShrink: 0,
              }}
            >
              Zadzwoń
            </a>
          )}
        </div>

        {showContractorEditor && task.contact && isOwner && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15,23,42,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1200,
              padding: 16,
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 360,
                background: "var(--color-bg-card)",
                borderRadius: "var(--radius-lg)",
                border: "1px solid var(--color-border)",
                padding: 16,
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-h3)", fontWeight: "var(--font-semibold)", color: "var(--color-text-primary)" }}>
                Edytuj wykonawcę
              </div>

              {contractorError && (
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-danger)" }}>{contractorError}</div>
              )}

              <input
                value={contractorDraft.name}
                onChange={(e) =>
                  setContractorDraft((d) => ({ ...d, name: e.target.value }))
                }
                placeholder="Nazwa *"
                style={{
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                }}
              />

              <PhoneInput
                defaultCountry="PL"
                international
                displayInitialValueAsLocal
                countryCallingCodeEditable={false}
                value={contractorDraft.phone || undefined}
                onChange={(value) =>
                  setContractorDraft((d) => ({ ...d, phone: value || "" }))
                }
                labels={phoneInputLabels}
                numberInputProps={{
                  placeholder: "Telefon",
                  style: {
                    border: "none",
                    outline: "none",
                    width: "100%",
                    minWidth: 0,
                    background: "transparent",
                  },
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                }}
              />

              <input
                value={contractorDraft.position}
                onChange={(e) =>
                  setContractorDraft((d) => ({ ...d, position: e.target.value }))
                }
                placeholder="Rola / stanowisko"
                style={{
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                }}
              />

              <input
                value={contractorDraft.company}
                onChange={(e) =>
                  setContractorDraft((d) => ({ ...d, company: e.target.value }))
                }
                placeholder="Firma"
                style={{
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                }}
              />

              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button
                  onClick={() => setShowContractorEditor(false)}
                  style={{
                    flex: 1,
                    border: "1px solid var(--color-border)",
                    background: "var(--btn-ghost-bg)",
                    color: "var(--btn-ghost-text)",
                    borderRadius: "var(--radius-md)",
                    padding: "9px 10px",
                    fontWeight: "var(--font-medium)",
                    fontSize: "var(--text-sm)",
                    cursor: "pointer",
                  }}
                >
                  Anuluj
                </button>
                <button
                  onClick={saveContractor}
                  disabled={savingContractor}
                  style={{
                    flex: 1,
                    border: "none",
                    background: "var(--color-primary)",
                    color: "var(--color-white)",
                    borderRadius: "var(--radius-md)",
                    padding: "9px 10px",
                    fontWeight: "var(--font-medium)",
                    fontSize: "var(--text-sm)",
                    cursor: savingContractor ? "default" : "pointer",
                  }}
                >
                  {savingContractor ? "Zapisywanie..." : "Zapisz"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showTaskEditor && isOwner && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15,23,42,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1200,
              padding: 16,
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 380,
                background: "var(--color-bg-card)",
                borderRadius: "var(--radius-lg)",
                border: "1px solid var(--color-border)",
                padding: 16,
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-h3)", fontWeight: "var(--font-semibold)", color: "var(--color-text-primary)" }}>
                Edytuj zadanie
              </div>

              {taskError && (
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-danger)" }}>{taskError}</div>
              )}

              <textarea
                value={taskDraft.desc}
                onChange={(e) => setTaskDraft((draft) => ({ ...draft, desc: e.target.value }))}
                rows={3}
                placeholder="Opis zadania *"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                  fontFamily: "inherit",
                  resize: "none",
                }}
              />

              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="date"
                  value={taskDraft.date}
                  onChange={(e) =>
                    setTaskDraft((draft) => ({
                      ...draft,
                      date: e.target.value,
                      time: e.target.value ? draft.time : "",
                    }))
                  }
                  style={{
                    flex: 1,
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "9px 10px",
                    fontSize: "var(--text-sm)",
                  }}
                />
                <select
                  value={splitTime(taskDraft.time).hour}
                  onChange={(e) => {
                    const current = splitTime(taskDraft.time);
                    setTaskDraft((draft) => ({ ...draft, time: getTimeValue(e.target.value, current.minute) }));
                  }}
                  disabled={!taskDraft.date}
                  style={{
                    flex: 1,
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "9px 10px",
                    fontSize: "var(--text-sm)",
                    opacity: taskDraft.date ? 1 : 0.6,
                  }}
                >
                  <option value="">--</option>
                  {hourOptions.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
                <select
                  value={splitTime(taskDraft.time).minute}
                  onChange={(e) => {
                    const current = splitTime(taskDraft.time);
                    setTaskDraft((draft) => ({ ...draft, time: getTimeValue(current.hour, e.target.value) }));
                  }}
                  disabled={!taskDraft.date}
                  style={{
                    flex: 1,
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "9px 10px",
                    fontSize: "var(--text-sm)",
                    opacity: taskDraft.date ? 1 : 0.6,
                  }}
                >
                  <option value="">--</option>
                  {minuteOptions.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>

              <select
                value={taskDraft.assignee}
                onChange={(e) => setTaskDraft((draft) => ({ ...draft, assignee: e.target.value }))}
                style={{
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  padding: "9px 10px",
                  fontSize: "var(--text-sm)",
                  fontFamily: "inherit",
                }}
              >
                <option value="unassigned">Nieprzypisane</option>
                <option value="self">Ja (Koordynator)</option>
                {contractors.map((contractor) => (
                  <option key={contractor.id} value={String(contractor.id)}>
                    {contractor.name}
                  </option>
                ))}
              </select>

              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button
                  onClick={() => setShowTaskEditor(false)}
                  style={{
                    flex: 1,
                    border: "1px solid var(--color-border)",
                    background: "var(--btn-ghost-bg)",
                    color: "var(--btn-ghost-text)",
                    borderRadius: "var(--radius-md)",
                    padding: "9px 10px",
                    fontWeight: "var(--font-medium)",
                    fontSize: "var(--text-sm)",
                    cursor: "pointer",
                  }}
                >
                  Anuluj
                </button>
                <button
                  onClick={saveTask}
                  disabled={savingTask}
                  style={{
                    flex: 1,
                    border: "none",
                    background: "var(--color-primary)",
                    color: "var(--color-white)",
                    borderRadius: "var(--radius-md)",
                    padding: "9px 10px",
                    fontWeight: "var(--font-medium)",
                    fontSize: "var(--text-sm)",
                    cursor: savingTask ? "default" : "pointer",
                  }}
                >
                  {savingTask ? "Zapisywanie..." : "Zapisz zmiany"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {deleteError && (
            <div
              style={{
                background: "var(--color-error-bg)",
                border: "1px solid var(--color-danger-border)",
                color: "var(--color-danger-dark)",
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: "var(--text-sm)",
              }}
            >
              {deleteError}
            </div>
          )}

          {task.status === "done" ? (
            <div
              style={{
              background: "var(--badge-done-bg)",
              border: "1px solid var(--badge-done-border)",
                borderRadius: "var(--radius-lg)",
                padding: 24,
                textAlign: "center",
              }}
            >
              <div style={{ marginBottom: 8, display: "inline-flex" }}>
                <UiIcon Icon={CheckCircle2} />
              </div>
              <div style={{ color: "var(--color-success)", fontWeight: "var(--font-semibold)", fontSize: "var(--text-body)" }}>
                Zadanie zakończone
              </div>
              <div style={{ color: "var(--color-text-muted)", fontSize: "var(--text-sm)", marginTop: 4 }}>
                Świetna robota!
              </div>
            </div>
          ) : (
            actions[task.status]?.map((a) => (
              <button
                key={a.next}
                onClick={() => onStatus(a.next)}
                style={{
                  background: a.bg,
                  color: a.color,
                  border: `1.5px solid ${a.border}`,
                  borderRadius: "var(--radius-lg)",
                  padding: 18,
                  fontWeight: "var(--font-semibold)",
                  fontSize: "var(--text-body)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                }}
              >
                <span style={{ display: "inline-flex" }}>
                  <UiIcon Icon={a.icon} />
                </span>
                {a.label}
              </button>
            ))
          )}

          {isOwner && (
            <button
              onClick={handleDeleteTask}
              disabled={deletingTask}
              style={{
                background: "var(--btn-destructive-bg)",
                color: "var(--btn-destructive-text)",
                border: "none",
                borderRadius: "var(--radius-lg)",
                padding: 14,
                fontWeight: "var(--font-medium)",
                fontSize: "var(--text-body)",
                cursor: deletingTask ? "default" : "pointer",
                fontFamily: "inherit",
              }}
            >
              {deletingTask ? (
                "Usuwanie..."
              ) : (
                <>
                  <InlineIcon Icon={Trash2} style={{ marginRight: 4 }} />
                  Usuń zadanie
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
